import { useContext, useEffect, useMemo, useCallback, useState } from 'react';
import { BaseNewAndDetail, getDarkThemeClass } from '../../utility';
import { useTheme } from '@emotion/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FarmContext } from '../../../context/FarmContext';
import { FeedbackContext } from '../../../App';
import apiService from '../../../api/apiService';
import _TransportersTab from './_TransportersTab';
import _VehiclesTab from './_VehiclesTab';
import _DestinationTab from './_DestinationTab';
import { MetrcTransportersBreadCrumbs } from '../../utility/BreadcrumbLinks';

export default function MetrcTransporters() {
  const [values, setValues] = useState({
    triggerAddNewDriver: false,
    triggerAddNewVehicle: false,
  });

  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');

  const { setFarmContextValue, farmContextValue } = useContext(FarmContext);
  const feedbackCX = useContext(FeedbackContext);
  const theme = useTheme();
  const navigate = useNavigate();

  const currDarkThemeClass = useMemo(() => getDarkThemeClass(theme), [theme]);

  const transportersKey = 'transporters';
  const vehiclesKey = 'vehicles';
  const destinationKey = 'destination';

  const baseEntityName = 'Transporters';

  const tabEntityMap = [
    { key: transportersKey, label: 'My Drivers', validForNew: true, defaultForDetail: true },
    { key: vehiclesKey, label: 'My Vehicles', validForNew: true, defaultForDetail: false },
    { key: destinationKey, label: 'Destinations', validForNew: true, defaultForDetail: false },
  ];

  const currentTab = tab || transportersKey;
  const tabLabels = {
    transporters: 'Drivers',
    vehicles: 'Vehicles',
    destination: 'Destination',
  };

  const currLinkLabel = tabLabels[currentTab] || 'Drivers';
  const baseSaveBtnChildren = `Add ${tabLabels[currentTab] || 'Drivers'}`;

  const breadcrumbsProps = {
    currLink: currLinkLabel,
    currDarkThemeClass,
    navigate,
  };
  const breadcrumbs = <MetrcTransportersBreadCrumbs {...breadcrumbsProps} />;

  const fetchedFarms = async () => {
    try {
      const response = await apiService.fetchInstance('farm/?fetch_farms=true');
      setFarmContextValue(response?.data || { farms: [] });
    } catch (error) {
      console.error('Error fetching farms', error);
      setFarmContextValue({ farms: [] });
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Error fetching farms.',
      });
    }
  };

  useEffect(() => {
    if (!farmContextValue || !farmContextValue?.farms?.length) {
      fetchedFarms();
    }
  }, [farmContextValue]);

  const handleSetValues = useCallback((prop, value) => {
    setValues(state => ({ ...state, [prop]: value }));
  }, []);

  const toDisable = () => false;

  const getDefaultTabForEndpoint = () => tabEntityMap?.find(tab => tab?.defaultForDetail)?.key;

  const handleSave = () => {
    if (currentTab === transportersKey) {
      handleSetValues('triggerAddNewDriver', true);
    } else if (currentTab === vehiclesKey) {
      handleSetValues('triggerAddNewVehicle', true);
    }
  };

  const transportersTabProps = {
    currDarkThemeClass,
    farmContextValue,
    triggerAddNewDriver: values.triggerAddNewDriver,
    handleSetValues,
  };

  const vehiclesTabProps = {
    currDarkThemeClass,
    farmContextValue,
    triggerAddNewVehicle: values.triggerAddNewVehicle,
    handleSetValues,
  };

  const DestinationTabProps = {
    currDarkThemeClass,
    farmContextValue,
    handleSetValues,
  };

  const tabContentMap = {
    [transportersKey]: <_TransportersTab {...transportersTabProps} />,
    [vehiclesKey]: <_VehiclesTab {...vehiclesTabProps} />,
    [destinationKey]: <_DestinationTab {...DestinationTabProps} />,
  };

  const tabCreateTitleMap = {
    [transportersKey]: 'Driver',
    [vehiclesKey]: 'Vehicle',
    // [destinationKey]: 'Destinations',
  };

  const showSaveBtn = tab => Object.keys(tabCreateTitleMap).includes(tab);

  const baseProps = {
    baseEntityName,
    baseid: undefined,
    currDarkThemeClass,
    tabContentMap,
    tabEntityMap,
    breadcrumbs,
    createBaseEntityMsg: '',
    baseSaveBtnChildren,
    baseEndpointCheck: false,
    enablePopup: true,
    showSaveBtn,
    handleSave,
    toDisable,
    getDefaultTabForEndpoint,
  };

  return <BaseNewAndDetail {...baseProps} />;
}
